import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { invoiceAPI } from '../lib/api';
import { formatPrice } from '../lib/utils';
import { 
  Loader2, CheckCircle, XCircle, FileText, 
  Calendar, Users, CreditCard, AlertCircle
} from 'lucide-react';

export default function InvoicePaymentPage() {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    loadInvoice();
    
    // Check if returning from payment
    const status = searchParams.get('status');
    const sessionId = searchParams.get('session_id');
    
    if (status === 'success' && sessionId) {
      pollPaymentStatus();
    } else if (status === 'cancelled') {
      setPaymentStatus('cancelled');
    }
  }, [invoiceId, searchParams]);

  const loadInvoice = async () => {
    try {
      const response = await invoiceAPI.get(invoiceId);
      setInvoice(response.data);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('paid');
      }
    } catch (error) {
      console.error('Error loading invoice:', error);
      setError('Invoice not found or has expired');
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setPaymentStatus('checking');
      return;
    }

    try {
      const response = await invoiceAPI.checkStatus(invoiceId);
      
      if (response.data.status === 'paid') {
        setPaymentStatus('paid');
        setInvoice(response.data.invoice);
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const response = await invoiceAPI.createCheckout(invoiceId);
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      setError('Failed to create payment session. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-muted/30">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="max-w-md p-8 text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Invoice Not Found</h2>
            <p className="text-muted-foreground">{error}</p>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Payment Success State
  if (paymentStatus === 'paid') {
    return (
      <div className="min-h-screen bg-muted/30">
        <Navbar />
        <div className="container mx-auto px-4 py-12">
          <Card className="max-w-lg mx-auto p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Payment Successful!</h1>
            <p className="text-muted-foreground mb-6">
              Thank you for your payment. A confirmation email will be sent shortly.
            </p>
            
            <Card className="bg-muted/50 p-4 text-left mb-6">
              <h3 className="font-semibold mb-2">{invoice?.tour_title}</h3>
              <div className="space-y-1 text-sm">
                <p><span className="text-muted-foreground">Invoice:</span> {invoice?.invoice_number}</p>
                <p><span className="text-muted-foreground">Amount Paid:</span> {formatPrice(invoice?.amount || 0)}</p>
                {invoice?.tour_date && (
                  <p><span className="text-muted-foreground">Tour Date:</span> {invoice.tour_date}</p>
                )}
              </div>
            </Card>

            <Button onClick={() => window.location.href = '/'} className="w-full">
              Return to Home
            </Button>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Payment Cancelled State
  if (paymentStatus === 'cancelled') {
    return (
      <div className="min-h-screen bg-muted/30">
        <Navbar />
        <div className="container mx-auto px-4 py-12">
          <Card className="max-w-lg mx-auto p-8 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="h-10 w-10 text-yellow-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Payment Cancelled</h1>
            <p className="text-muted-foreground mb-6">
              Your payment was cancelled. You can try again below.
            </p>
            
            <Button onClick={handlePayment} disabled={processing} className="w-full">
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CreditCard className="h-4 w-4 mr-2" />
              )}
              Try Payment Again
            </Button>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // Default Invoice Payment Page
  return (
    <div className="min-h-screen bg-muted/30">
      <Navbar />
      <div className="container mx-auto px-4 py-12">
        <Card className="max-w-lg mx-auto overflow-hidden" data-testid="invoice-payment-card">
          {/* Header */}
          <div className="bg-primary text-white p-6">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8" />
              <div>
                <h1 className="text-xl font-bold">Invoice Payment</h1>
                <p className="text-white/80 text-sm">{invoice?.invoice_number}</p>
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="p-6">
            <h2 className="text-xl font-bold mb-1">{invoice?.tour_title}</h2>
            <p className="text-muted-foreground mb-6">For: {invoice?.customer_name}</p>

            <div className="space-y-4 mb-6">
              {invoice?.tour_date && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Tour Date</p>
                    <p className="font-medium">{invoice.tour_date}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Guests</p>
                  <p className="font-medium">{invoice?.guests} {invoice?.guests === 1 ? 'person' : 'people'}</p>
                </div>
              </div>
            </div>

            {/* Amount */}
            <div className="bg-muted/50 rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Amount Due</span>
                <span className="text-3xl font-bold text-primary">
                  {formatPrice(invoice?.amount || 0)}
                </span>
              </div>
              {invoice?.due_date && (
                <p className="text-sm text-muted-foreground mt-2">
                  Due by: {invoice.due_date}
                </p>
              )}
            </div>

            {/* Pay Button */}
            <Button 
              onClick={handlePayment} 
              disabled={processing || invoice?.payment_status === 'paid'}
              className="w-full h-12 text-lg"
              data-testid="pay-invoice-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-5 w-5 mr-2" />
                  Pay {formatPrice(invoice?.amount || 0)}
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground mt-4">
              Secure payment powered by Stripe
            </p>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
}
